import type { AggregateChange, AggregateClient, EventBatch, WireEvent } from '@celeriant/wire';
import { Forbidden, NotExist } from '@celeriant/wire';
import { freeze } from 'immer';
import type { AggregateDef, AnyEventDef, UndoEvent } from './aggregate.js';
import { assert } from './assert.js';
import {
  checkPrecondition,
  foldEvents,
  initialFrozenState,
  lookupKind,
  undoOf,
  validateIncomingEvent,
  UnknownEventKindError,
  type AnyMutation,
} from './fold.js';
import { Mutex } from './mutex.js';
import { Notifier, type Listener, type Unsubscribe } from './notifier.js';
import { StatusTracker, type SyncStatus } from './status.js';
import type { Store, UnitOfWork } from './store.js';
import type { AggregateRecord, NewEvent, StoredEvent } from './storeTypes.js';
import { UndoStack } from './undoStack.js';
import { awaitPace, pacedUntil } from './writePacing.js';

// The local-first engine for one aggregate instance. The log is the outbox: an event is appended
// once, folded once in local insertion order, and "unsent" is the query si === 0. Every unit of
// work is one Store transaction, then one notification, then side effects. Three mutexes: read
// serialises pulls, write serialises sends, apply serialises state changes. read and apply never
// nest under each other's callers in the wrong order; write may take read then apply.

export interface EngineCryptoProvider {
  encryptEvent(type: string, id: string, plaintext: string): Promise<{ iv: string; value: string }>;
  decryptEvent(type: string, id: string, iv: string, ciphertext: string): Promise<string>;
  // Declared only by providers whose key is fallible (passphrase-derived). The engine then holds
  // sends while the key is absent and runs a verifying read before the first encrypted drain: a
  // mistyped passphrase derives a valid-looking key, and ciphertext published under it would be
  // unreadable to every correct key.
  hasKey?(type: string, id: string): boolean;
}

export type DeletedReason = 'deleted' | 'access-revoked';

export interface SyncEngineOptions<TState> {
  def: AggregateDef<TState, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  aggregateId: string;
  store: Store;
  gateway: AggregateClient;
  clientId: string;
  userId?: string | undefined;
  now?: (() => number) | undefined;
  crypto?: EngineCryptoProvider | undefined;
  onDeleted?: ((reason: DeletedReason) => void) | undefined;
  // Fired after every locally persisted event (commit, undo, redo): the scheduler's cue to send.
  onLocalCommit?: (() => void) | undefined;
  onUpdateRequired?: (() => void) | undefined;
  isOnline?: (() => boolean) | undefined;
  // 'closed' means zero network contact until openSyncGate().
  syncGate?: 'open' | 'closed' | undefined;
}

export interface PullOptions {
  shareIdHash?: string | undefined;
}

const SEND_ROUNDS_MAX = 1_000;
const BATCHES_PER_RECEIVE_MAX = 100_000;

interface CommitOutcome<TState> {
  readonly record: AggregateRecord;
  readonly state: TState;
  readonly results: unknown[];
  readonly ownResult: unknown;
  // Derived inside the transaction, so an undo that throws aborts the commit.
  readonly undo: UndoEvent | undefined;
}

interface PreparedBatches {
  readonly rows: NewEvent[];
  readonly maxVersion: number;
}

export class SyncEngine<TState> {
  private readonly def: AggregateDef<TState, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  private readonly type: string;
  private readonly id: string;
  private readonly store: Store;
  private readonly gateway: AggregateClient;
  private readonly clientId: string;
  private readonly userId: string | undefined;
  private readonly now: () => number;
  private readonly crypto: EngineCryptoProvider | undefined;
  private readonly onDeleted: ((reason: DeletedReason) => void) | undefined;
  private readonly onLocalCommit: (() => void) | undefined;
  private readonly onUpdateRequired: (() => void) | undefined;
  private lastShareIdHash: string | undefined;

  private readonly readMutex = new Mutex();
  private readonly writeMutex = new Mutex();
  private readonly applyMutex = new Mutex();

  private readonly notifier: Notifier<TState, unknown>;
  private readonly undoStack = new UndoStack<AnyMutation>();
  private record: AggregateRecord;
  private seedAttempted = false;
  private closed = false;
  private writeNotBefore = 0;
  private keyVerifiedRead = false;
  private syncGateOpen: boolean;
  private updateRequiredFlag = false;

  private readonly isOnline: () => boolean;
  private readonly status: StatusTracker;
  private inFlight = 0;
  private pendingCount = 0;
  private lastSyncedAt: number | undefined;
  private lastError: string | undefined;

  constructor(options: SyncEngineOptions<TState>) {
    if ('snapshotEvery' in options) throw new Error('SyncEngine: snapshotEvery is gone; there is no snapshot cadence');
    this.def = options.def;
    this.type = options.def.name;
    this.id = options.aggregateId;
    assert(this.id.length > 0, 'SyncEngine: aggregateId is set');
    this.store = options.store;
    this.gateway = options.gateway;
    this.clientId = options.clientId;
    assert(this.clientId.length > 0, 'SyncEngine: clientId is set');
    this.userId = options.userId;
    this.now = options.now ?? Date.now;
    this.crypto = options.crypto;
    this.onDeleted = options.onDeleted;
    this.onLocalCommit = options.onLocalCommit;
    this.onUpdateRequired = options.onUpdateRequired;
    this.syncGateOpen = options.syncGate !== 'closed';
    this.isOnline = options.isOnline ?? (() => true);
    this.status = new StatusTracker(() => ({
      online: this.isOnline(),
      syncing: this.inFlight > 0,
      pending: this.pendingCount,
      updateRequired: this.updateRequiredFlag,
      lastSyncedAt: this.lastSyncedAt,
      lastError: this.lastError,
    }));
    const encryptsSomething = this.def.encrypt || Object.values(this.def.encryptKinds).some((flag) => flag);
    if (encryptsSomething && this.crypto === undefined) {
      throw new Error(`SyncEngine: aggregate "${this.def.name}" declares encrypt:true but no crypto provider was supplied`);
    }
    this.notifier = new Notifier<TState, unknown>(initialFrozenState(options.def));
    this.record = this.freshRecord();
  }

  get state(): TState { return this.notifier.value; }
  subscribe(listener: Listener<TState, unknown>): Unsubscribe { return this.notifier.subscribe(listener); }
  get canUndo(): boolean { return this.undoStack.canUndo; }
  get canRedo(): boolean { return this.undoStack.canRedo; }
  get updateRequired(): boolean { return this.updateRequiredFlag; }
  get serverVersion(): number { return this.record.serverVersion; }
  get syncGate(): 'open' | 'closed' { return this.syncGateOpen ? 'open' : 'closed'; }
  get statusValue(): SyncStatus { return this.status.value; }
  subscribeStatus(cb: (s: SyncStatus) => void): Unsubscribe { return this.status.subscribe(cb); }
  notifyConnectivityChanged(): void { this.status.emit(); }

  // Hydrates from the state document and folds whatever the log holds above it. Replay from the
  // log happens in exactly two cases: no state document for a non-empty log (a storage upgrade),
  // and a schema mismatch with no `migrate`. No network.
  async open(): Promise<void> {
    if (this.closed) throw new Error('SyncEngine: closed');
    await this.applyMutex.runExclusive(async () => {
      const loaded = await this.store.unitOfWork(this.type, this.id, 'readonly', async (uow) => {
        const record = await uow.getRecord();
        const state = await uow.getState();
        // The record's appliedLi, not the state document's: rows the fold left unchanged advance
        // the record without a state write, and replaying them would cost reducer calls for
        // nothing. Only a schema bump with no migrate needs the log from the start.
        const replayAll = state !== undefined && state.schemaVersion !== this.def.schemaVersion && this.def.migrate === undefined;
        const tail = await uow.readTail(record === undefined || replayAll ? 0 : record.appliedLi);
        return { record, state, tail };
      });
      if (loaded.record !== undefined) this.record = loaded.record;
      if (loaded.state !== undefined) assert(loaded.state.appliedLi <= this.record.appliedLi, 'open: the state document never leads the record');
      const initial = initialFrozenState(this.def);
      let hydrated: TState;
      if (loaded.state === undefined) {
        // No state document means every folded row was a no-op, or the log was carried without a
        // snapshot (appliedLi 0): the first is a tail fold, the second a replay.
        hydrated = this.record.appliedLi === 0 && loaded.tail.length > 0
          ? this.reprojectFromLog(initial, loaded.tail)
          : this.foldTailOrLatch(initial, loaded.tail);
      } else if (loaded.state.schemaVersion === this.def.schemaVersion) {
        hydrated = this.foldTailOrLatch(freeze(loaded.state.state as TState, true), loaded.tail);
      } else if (this.def.migrate !== undefined) {
        const migrated = freeze(this.def.migrate(loaded.state.state, loaded.state.schemaVersion), true);
        hydrated = this.foldTailOrLatch(migrated, loaded.tail);
      } else {
        if (this.record.trimFloor > 1) throw new CannotReproject(this.type, this.id, loaded.state.schemaVersion, this.def.schemaVersion);
        hydrated = this.reprojectFromLog(initial, loaded.tail);
      }
      const lastLi = loaded.tail.length === 0 ? this.record.appliedLi : loaded.tail[loaded.tail.length - 1]!.li;
      assert(lastLi >= this.record.appliedLi, 'open: the log is never behind the record');
      this.record = { ...this.record, appliedLi: lastLi, lastLi: Math.max(this.record.lastLi, lastLi) };
      this.notifier.reset(hydrated);
    });
    this.pendingCount = await this.countPending();
    this.status.emit();
  }

  async close(): Promise<void> {
    this.closed = true;
  }

  async commit(mutation: AnyMutation): Promise<void> {
    this.assertWritable();
    await this.applyMutex.runExclusive(() => this.commitLocked(mutation, false));
  }

  commitLocal(mutation: AnyMutation): Promise<void> {
    return this.commit(mutation);
  }

  async undo(): Promise<boolean> {
    this.assertWritable();
    return this.applyMutex.runExclusive(async () => {
      const pair = this.undoStack.popUndo();
      if (pair === undefined) return false;
      try {
        await this.commitLocked(pair.undo, true);
      } catch (e: unknown) {
        this.undoStack.restoreUndo(pair);
        throw e;
      }
      return true;
    });
  }

  // Re-commits the original as a new event; the fresh result yields the fresh undo pair.
  async redo(): Promise<boolean> {
    this.assertWritable();
    return this.applyMutex.runExclusive(async () => {
      const pair = this.undoStack.popRedo();
      if (pair === undefined) return false;
      let outcome: CommitOutcome<TState>;
      try {
        outcome = await this.commitLocked(pair.original, true);
      } catch (e: unknown) {
        this.undoStack.restoreRedo(pair);
        throw e;
      }
      if (outcome.undo !== undefined) this.undoStack.pushRedone(pair.original, outcome.undo);
      return true;
    });
  }

  // Drains the unsent rows as one batch per round until none remain. Holds while update-required
  // or gated (the rows drain later), and for a fallible key until a read has verified it.
  async send(): Promise<void> {
    if (this.closed || this.updateRequiredFlag || !this.syncGateOpen) return;
    if (this.crypto?.hasKey !== undefined && this.encryptsSomething() && !this.keyVerifiedRead) {
      if (!this.crypto.hasKey(this.type, this.id)) return;
      try {
        await this.backgroundPull();
      } catch (e: unknown) {
        // A brand-new aggregate has no readable history until the create write lands, so there
        // is nothing to verify against and nothing to poison.
        if (e instanceof Forbidden && this.record.serverVersion === 0) this.keyVerifiedRead = true;
        else throw e;
      }
      if (!this.keyVerifiedRead) return;
    }
    const tracked = this.pendingCount > 0;
    if (tracked) this.enterSync();
    try {
      await this.writeMutex.runExclusive(async () => {
        await this.ensureSeeded();
        for (let round = 0; round < SEND_ROUNDS_MAX; round += 1) {
          if (this.closed) return;
          const unsent = await this.store.unitOfWork(this.type, this.id, 'readonly', (uow) => uow.readUnsent());
          if (unsent.length === 0) return;
          await this.attemptSend(unsent);
        }
        throw new Error(`SyncEngine: unsent events kept appearing for ${SEND_ROUNDS_MAX} send rounds`);
      });
      if (tracked) this.lastError = undefined;
    } catch (e: unknown) {
      if (tracked) this.lastError = e instanceof Error ? e.message : String(e);
      throw e;
    } finally {
      if (tracked) this.exitSync();
    }
  }

  // Reads from the cursor plus one and folds what comes back. A NotExist or Forbidden on a plain
  // pull after this replica has synced means the aggregate is gone or access was revoked; the
  // replica is purged and the consumer told. A share-scoped pull rethrows instead, because a 403
  // there can be share machinery rather than revocation.
  async pull(opts: PullOptions = {}): Promise<void> {
    if (this.closed || !this.syncGateOpen) return;
    if (opts.shareIdHash !== undefined) this.lastShareIdHash = opts.shareIdHash;
    const cursorBefore = this.record.serverVersion;
    assert(cursorBefore >= 0, 'pull: the cursor is a version');
    let purgeReason: DeletedReason | undefined;
    this.enterSync();
    try {
      await this.readMutex.runExclusive(async () => {
        const readOpts: { fromVersion: number; shareIdHash?: string } = { fromVersion: this.record.serverVersion + 1 };
        if (opts.shareIdHash !== undefined) readOpts.shareIdHash = opts.shareIdHash;
        let batches: EventBatch[];
        try {
          batches = await this.gateway.read(readOpts);
        } catch (e: unknown) {
          const plainAndSynced = this.record.serverVersion > 0 && opts.shareIdHash === undefined;
          if (plainAndSynced && e instanceof NotExist) { purgeReason = 'deleted'; return; }
          if (plainAndSynced && e instanceof Forbidden) { purgeReason = 'access-revoked'; return; }
          throw e;
        }
        if (this.closed) return;
        if (batches.length === 0) return;
        await this.receive(batches);
      });
      if (purgeReason === undefined) {
        this.keyVerifiedRead = true;
        this.lastSyncedAt = this.now();
        this.lastError = undefined;
        this.status.emit();
      }
    } catch (e: unknown) {
      this.lastError = e instanceof Error ? e.message : String(e);
      this.status.emit();
      throw e;
    } finally {
      this.exitSync();
    }
    assert(this.record.serverVersion >= cursorBefore, 'pull: the cursor never moves back');
    if (purgeReason !== undefined) await this.purgeAndNotify(purgeReason);
  }

  // The scheduler's trigger. Reuses the share scope of the last pull so a share-joined replica
  // is never purged by a blind plain read, and holds quietly while a fallible key is absent.
  async backgroundPull(): Promise<void> {
    if (this.encryptsSomething() && this.crypto?.hasKey?.(this.type, this.id) === false) return;
    await this.pull(this.lastShareIdHash !== undefined ? { shareIdHash: this.lastShareIdHash } : {});
  }

  // Sibling wake: a tab that shares this storage wrote. Fold the tail above what this tab has
  // applied and notify. Nothing is written; the sibling wrote.
  async refreshFromStore(): Promise<void> {
    if (this.closed) return;
    await this.applyMutex.runExclusive(async () => {
      if (this.closed) return;
      const loaded = await this.store.unitOfWork(this.type, this.id, 'readonly', async (uow) => {
        const record = await uow.getRecord();
        const tail = await uow.readTail(this.record.appliedLi);
        return { record, tail };
      });
      if (loaded.record !== undefined) this.record = this.mergeRecord(loaded.record);
      if (loaded.tail.length === 0) return;
      const folded = foldEvents(this.def, this.notifier.value, loaded.tail, true);
      this.record = { ...this.record, appliedLi: loaded.tail[loaded.tail.length - 1]!.li };
      this.notifier.set(folded.state, folded.results);
    });
    this.pendingCount = await this.countPending();
    this.status.emit();
  }

  // Flips the gate, then catches up. The flip is the contract: a transient failure on that first
  // read is logged and swallowed, never surfaced to the caller, so the root's subscribe and drain
  // follow the flip and the next send or poke retries the read.
  async openSyncGate(opts: PullOptions = {}): Promise<void> {
    if (this.syncGateOpen || this.closed) return;
    this.syncGateOpen = true;
    if (opts.shareIdHash !== undefined) this.lastShareIdHash = opts.shareIdHash;
    try {
      await this.pull(opts);
    } catch (e: unknown) {
      console.error(`SyncEngine: openSyncGate catch-up pull failed for "${this.type}/${this.id}":`, e);
    }
    assert(this.syncGateOpen, 'openSyncGate: the gate stays open whatever the catch-up did');
  }

  // A realtime poke. Write and create mean pull; trim and delete have their own paths; an
  // operation this build does not know is ignored, never a read, because a forward-compatible
  // op turned into a spurious full read is the bug this shape exists to avoid.
  async applyChange(change: AggregateChange): Promise<void> {
    assert(change.aggregateId === this.id, 'applyChange: the change names this aggregate');
    switch (change.operation) {
      case 'write':
      case 'create':
        await this.pull();
        return;
      case 'trim':
        if (change.keepFromVersion !== undefined) await this.handleTrim(change.keepFromVersion);
        return;
      case 'delete':
        await this.handleDelete();
        return;
    }
  }

  // Mirrors the server's trim. The state document already folds everything below the floor, so
  // rows with 0 < si < keepFrom go and nothing is replayed.
  async handleTrim(keepFromVersion: number): Promise<void> {
    if (this.closed) return;
    if (keepFromVersion <= 1) return;
    await this.readMutex.runExclusive(() => this.applyMutex.runExclusive(async () => {
      if (this.closed) return;
      const updated = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
        const record = this.mergeRecord(await uow.getRecord());
        await uow.trim(keepFromVersion);
        const next: AggregateRecord = { ...record, trimFloor: Math.max(record.trimFloor, keepFromVersion), modifiedAt: this.now() };
        await uow.putRecord(next);
        return next;
      });
      this.record = updated;
    }));
  }

  async handleDelete(): Promise<void> {
    await this.purgeAndNotify('deleted');
  }

  // Returns the own event's result (undefined when it folded to nothing) and its undo event.
  // `fromStack` skips the precondition, the undo push and the redo clear: the event is an undo
  // or redo of something that already happened.
  private async commitLocked(mutation: AnyMutation, fromStack: boolean): Promise<CommitOutcome<TState>> {
    if (this.closed) throw new Error('SyncEngine: closed');
    const eventDef = lookupKind<AnyEventDef<TState>>(this.def.events, mutation.kind);
    if (eventDef === undefined) throw new Error(`unknown event kind for "${this.type}": ${mutation.kind}`);
    const payload: unknown = eventDef.payload.parse(mutation.payload);
    const now = this.now();
    const wireType = this.def.wireTypes[mutation.kind];
    assert(wireType !== undefined, 'commit: every declared kind has a wire type');
    const before = this.notifier.value;
    const outcome = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow): Promise<CommitOutcome<TState>> => {
      const record = this.mergeRecord(await uow.getRecord());
      const tail = await uow.readTail(record.appliedLi);
      const sibling = foldEvents(this.def, before, tail, true);
      if (!fromStack && !checkPrecondition(this.def, sibling.state, { kind: mutation.kind, payload })) {
        throw new PreconditionFailed(mutation.kind);
      }
      // Both counters come off the record read in this transaction, so sibling tabs can never
      // allocate the same clientSeq or li.
      const clientSeq = record.nextClientSeq + 1;
      assert(clientSeq > record.lastSentClientSeq, 'commit: a fresh clientSeq is above everything sent');
      const li = record.lastLi + 1;
      assert(li > record.appliedLi, 'commit: a fresh li is above everything applied');
      const row: NewEvent = {
        type: this.type, id: this.id, si: 0, clientSeq, clientId: this.clientId, userId: this.userId,
        ts: now, serverTs: 0, typeMajor: wireType.major, typeMinor: wireType.minor,
        value: JSON.stringify({ kind: mutation.kind, payload }),
      };
      const appended = await uow.append([row], li);
      const own = foldEvents(this.def, sibling.state, appended, true);
      // Before any put: an undo that returns an undeclared kind throws here and the transaction
      // aborts, so the log never holds an event this device cannot stand behind.
      const undo = own.results.length === 0 || eventDef.undo === undefined
        ? undefined
        : undoOf(this.def, mutation.kind, eventDef, own.results[0]);
      if (own.state !== before) {
        await uow.putState({ type: this.type, id: this.id, schemaVersion: this.def.schemaVersion, appliedLi: li, state: own.state });
      }
      const next: AggregateRecord = {
        ...record, nextClientSeq: clientSeq, lastLi: li, appliedLi: li,
        createdAt: record.createdAt === 0 ? now : record.createdAt, modifiedAt: now,
      };
      await uow.putRecord(next);
      return { record: next, state: own.state, results: [...sibling.results, ...own.results], ownResult: own.results[0], undo };
    });
    this.record = outcome.record;
    this.pendingCount += 1;
    if (!fromStack) {
      if (outcome.undo === undefined) this.undoStack.clearRedo();
      else this.undoStack.push({ kind: mutation.kind, payload }, outcome.undo);
    }
    this.notifier.set(outcome.state, outcome.results);
    this.status.emit();
    this.onLocalCommit?.();
    return outcome;
  }

  // Validates the whole read before anything is persisted (invariant 31): one unfoldable event
  // refuses every batch and latches update-required. Then one transaction appends, folds and
  // advances the cursor, and one notification carries the results.
  private async receive(batches: EventBatch[]): Promise<void> {
    assert(batches.length <= BATCHES_PER_RECEIVE_MAX, `receive: ${batches.length} batches exceeds ${BATCHES_PER_RECEIVE_MAX}`);
    const prepared = await this.prepareBatches(batches);
    if (prepared === undefined) {
      this.markUpdateRequired();
      return;
    }
    if (prepared.maxVersion <= this.record.serverVersion) return;
    await this.applyMutex.runExclusive(async () => {
      if (this.closed) return;
      const before = this.notifier.value;
      const outcome = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
        const record = this.mergeRecord(await uow.getRecord());
        const tail = await uow.readTail(record.appliedLi);
        const sibling = foldEvents(this.def, before, tail, true);
        // A sibling tab may have folded part of this read already; its rows are in the tail.
        const rows = prepared.rows.filter((r) => r.si > record.serverVersion);
        const stored = await uow.append(rows, record.lastLi + 1);
        const folded = foldEvents(this.def, sibling.state, stored, true);
        const lastLi = lastLiOf(stored, lastLiOf(tail, record.appliedLi));
        assert(lastLi >= record.appliedLi, 'receive: appliedLi never moves back');
        if (folded.state !== before) {
          await uow.putState({ type: this.type, id: this.id, schemaVersion: this.def.schemaVersion, appliedLi: lastLi, state: folded.state });
        }
        const next: AggregateRecord = {
          ...record, serverVersion: Math.max(record.serverVersion, prepared.maxVersion), lastLi: Math.max(record.lastLi, lastLi), appliedLi: lastLi, modifiedAt: this.now(),
        };
        await uow.putRecord(next);
        return { record: next, state: folded.state, results: [...sibling.results, ...folded.results] };
      });
      this.record = outcome.record;
      this.notifier.set(outcome.state, outcome.results);
    });
  }

  // Decrypts and validates outside the transaction. Returns undefined when any event cannot be
  // folded by this build. Dedup is two rules: a batch at or below the running cursor is a
  // re-delivery, and an own event at or below lastSentClientSeq is an echo of a row the log
  // already holds (an own_events read). Sibling tabs cannot collide on anything else, because
  // clientSeq and li are allocated inside the shared transaction.
  private async prepareBatches(batches: EventBatch[]): Promise<PreparedBatches | undefined> {
    const rows: NewEvent[] = [];
    let cursor = this.record.serverVersion;
    const ownEcho = this.record.lastSentClientSeq;
    for (const batch of batches) {
      if (batch.aggregateVersion <= cursor) continue;
      cursor = batch.aggregateVersion;
      const own = batch.clientId === this.clientId;
      for (const ev of batch.events) {
        if (own && ev.client_seq <= ownEcho) continue;
        let value = ev.event_value;
        if (ev.iv !== undefined) {
          if (this.crypto === undefined) {
            console.error(`SyncEngine: refusing event with iv for unencrypted "${this.type}/${this.id}"`);
            return undefined;
          }
          value = await this.crypto.decryptEvent(this.type, this.id, ev.iv, ev.event_value);
        }
        if (!validateIncomingEvent(this.def, value, ev.event_type_minor)) return undefined;
        const kind = (JSON.parse(value) as { kind: string }).kind;
        const mustBeEncrypted = lookupKind(this.def.encryptKinds, kind) ?? this.def.encrypt;
        if (mustBeEncrypted && ev.iv === undefined) {
          console.error(`SyncEngine: refusing plaintext event of encrypted kind "${kind}" for "${this.type}/${this.id}" (downgrade attack?)`);
          return undefined;
        }
        rows.push({
          type: this.type, id: this.id, si: batch.aggregateVersion, clientSeq: ev.client_seq,
          clientId: batch.clientId, userId: batch.userId, ts: ev.event_timestamp, serverTs: batch.serverTimestamp,
          typeMajor: ev.event_type_major, typeMinor: ev.event_type_minor, value,
        });
      }
    }
    assert(cursor >= this.record.serverVersion, 'prepareBatches: the cursor never moves back');
    assert(rows.every((r) => r.si > this.record.serverVersion), 'prepareBatches: every row is above the cursor');
    return { rows, maxVersion: cursor };
  }

  private async attemptSend(unsent: StoredEvent[]): Promise<void> {
    assert(unsent.length > 0, 'attemptSend: something to send');
    const wire = await Promise.all(unsent.map((e) => this.toWire(e)));
    assert(wire.length === unsent.length, 'attemptSend: one wire event per row');
    await awaitPace(this.writeNotBefore, this.now);
    if (this.closed) return;
    const result = await this.gateway.write(wire, { createIfNotExist: this.record.serverVersion === 0 });
    assert(result.aggregateVersion >= 1, 'attemptSend: the server assigned a version');
    this.writeNotBefore = pacedUntil(this.now(), result.writeBackoffMs);
    await this.ackSend(unsent, result.aggregateVersion);
  }

  private async toWire(event: StoredEvent): Promise<WireEvent> {
    const kind = (JSON.parse(event.value) as { kind: string }).kind;
    const base: WireEvent = {
      client_seq: event.clientSeq,
      event_seq: 0,
      event_timestamp: event.ts,
      event_type_major: event.typeMajor,
      event_type_minor: event.typeMinor,
      event_value: event.value,
    };
    const encrypt = lookupKind(this.def.encryptKinds, kind) ?? this.def.encrypt;
    if (encrypt) {
      assert(this.crypto !== undefined, 'toWire: an encrypting kind has a provider');
      const sealed = await this.crypto.encryptEvent(this.type, this.id, event.value);
      base.event_value = sealed.value;
      base.iv = sealed.iv;
    }
    return base;
  }

  // Stamps si on the sent rows and applies the cursor rule (WIRE-CONTRACT, write route): the
  // cursor advances only through folded versions, with the one provable exception of a write
  // landing at cursor + 1. Otherwise peers may sit in the gap, so they are pulled first.
  private async ackSend(sent: StoredEvent[], aggregateVersion: number): Promise<void> {
    assert(aggregateVersion > 0, 'ackSend: the server assigned a version');
    let gapFree = false;
    await this.applyMutex.runExclusive(async () => {
      if (this.closed) return;
      gapFree = aggregateVersion === this.record.serverVersion + 1;
      const lastSeq = sent[sent.length - 1]!.clientSeq;
      const updated = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
        const record = this.mergeRecord(await uow.getRecord());
        await uow.stampSi(sent.map((e) => e.li), aggregateVersion);
        const next: AggregateRecord = {
          ...record,
          lastSentClientSeq: Math.max(record.lastSentClientSeq, lastSeq),
          serverVersion: gapFree ? aggregateVersion : record.serverVersion,
          modifiedAt: this.now(),
        };
        await uow.putRecord(next);
        return next;
      });
      this.record = updated;
      this.pendingCount = Math.max(0, this.pendingCount - sent.length);
      this.lastSyncedAt = this.now();
      this.status.emit();
    });
    if (this.closed || gapFree) return;
    await this.readMutex.runExclusive(async () => {
      const peers = await this.gateway.read({ fromVersion: this.record.serverVersion + 1 });
      if (this.closed) return;
      if (peers.length > 0) await this.receive(peers);
      if (this.record.serverVersion < aggregateVersion) {
        await this.applyMutex.runExclusive(async () => {
          if (this.closed) return;
          const updated = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
            const record = this.mergeRecord(await uow.getRecord());
            const next: AggregateRecord = { ...record, serverVersion: Math.max(record.serverVersion, aggregateVersion), modifiedAt: this.now() };
            await uow.putRecord(next);
            return next;
          });
          this.record = updated;
        });
      }
    });
    assert(this.closed || this.record.serverVersion >= aggregateVersion, 'ackSend: the cursor covers the own write');
  }

  // First send per session with nothing ever sent: one own_events read seeds the client_seq
  // high-water. An unsent row the server already holds is an ack that never landed, so it is
  // stamped rather than resent; the rest are renumbered above the server's high-water so the
  // gateway's dedup cannot swallow them. The read then folds like any other, which recovers the
  // history a wiped store lost; lastSentClientSeq rises only after that fold, because the echo
  // rule in prepareBatches would otherwise drop the very rows being recovered.
  private async ensureSeeded(): Promise<void> {
    if (!this.syncGateOpen || this.seedAttempted) return;
    this.seedAttempted = true;
    if (this.record.lastSentClientSeq > 0) return;
    let batches: EventBatch[];
    try {
      const readOpts: { fromVersion: number; ownEvents: boolean; shareIdHash?: string } = { fromVersion: this.record.serverVersion + 1, ownEvents: true };
      if (this.lastShareIdHash !== undefined) readOpts.shareIdHash = this.lastShareIdHash;
      batches = await this.gateway.read(readOpts);
    } catch (e: unknown) {
      // A 403 will not change on retry (no grant yet); anything else is transient.
      if (!(e instanceof Forbidden)) this.seedAttempted = false;
      return;
    }
    if (this.closed) return;
    const ownOnServer = new Map<number, { version: number; event: WireEvent }>();
    for (const b of batches) {
      if (b.clientId !== this.clientId) continue;
      for (const ev of b.events) ownOnServer.set(ev.client_seq, { version: b.aggregateVersion, event: ev });
    }
    let maxSeq = 0;
    for (const seq of ownOnServer.keys()) if (seq > maxSeq) maxSeq = seq;
    if (maxSeq === 0) {
      if (batches.length > 0) await this.receive(batches);
      return;
    }
    const ackedSeqs = new Set<number>();
    await this.applyMutex.runExclusive(async () => {
      if (this.closed) return;
      const outcome = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
        const record = this.mergeRecord(await uow.getRecord());
        const unsent = await uow.readUnsent();
        const lisByVersion = new Map<number, number[]>();
        for (const row of unsent) {
          const held = ownOnServer.get(row.clientSeq);
          if (held === undefined || !sameEvent(row, held.event)) continue;
          const lis = lisByVersion.get(held.version);
          if (lis === undefined) lisByVersion.set(held.version, [row.li]);
          else lis.push(row.li);
          ackedSeqs.add(row.clientSeq);
        }
        for (const [version, lis] of lisByVersion) await uow.stampSi(lis, version);
        const high = await uow.renumberUnsent(maxSeq);
        assert(ackedSeqs.size <= unsent.length, 'ensureSeeded: acked rows are unsent rows');
        const next: AggregateRecord = { ...record, nextClientSeq: Math.max(record.nextClientSeq, high, maxSeq), modifiedAt: this.now() };
        await uow.putRecord(next);
        return { record: next };
      });
      this.record = outcome.record;
      this.pendingCount = Math.max(0, this.pendingCount - ackedSeqs.size);
      this.status.emit();
    });
    if (this.closed) return;
    const recovery = batches.map((b) => (b.clientId === this.clientId
      ? { ...b, events: b.events.filter((ev) => !ackedSeqs.has(ev.client_seq)) }
      : b));
    await this.receive(recovery);
    if (this.closed) return;
    await this.applyMutex.runExclusive(async () => {
      if (this.closed) return;
      const updated = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
        const record = this.mergeRecord(await uow.getRecord());
        const next: AggregateRecord = { ...record, lastSentClientSeq: Math.max(record.lastSentClientSeq, maxSeq), modifiedAt: this.now() };
        await uow.putRecord(next);
        return next;
      });
      this.record = updated;
    });
    assert(this.closed || this.record.lastSentClientSeq >= maxSeq, 'ensureSeeded: the high-water is seeded');
  }

  // Terminal for this instance: an in-flight send confirming after the wipe must not resurrect
  // the record. The root evicts the engine on onDeleted and a reopen builds a fresh one.
  private async purgeAndNotify(reason: DeletedReason): Promise<void> {
    if (this.closed) return;
    await this.readMutex.runExclusive(() => this.applyMutex.runExclusive(async () => {
      await this.store.unitOfWork(this.type, this.id, 'readwrite', (uow: UnitOfWork) => uow.purge());
      this.record = this.freshRecord();
      this.undoStack.clear();
      this.pendingCount = 0;
      this.notifier.set(initialFrozenState(this.def), []);
      this.status.emit();
    }));
    this.closed = true;
    this.onDeleted?.(reason);
  }

  // Full replay from the start of the log. Two callers, both in open().
  private reprojectFromLog(base: TState, log: ReadonlyArray<StoredEvent>): TState {
    assert(log.length === 0 || log[0]!.li >= 1, 'reprojectFromLog: replays from the start of the log');
    const state = this.foldTailOrLatch(base, log);
    assert(this.updateRequiredFlag || log.length === 0 || state !== base || Object.isFrozen(state), 'reprojectFromLog: produced a state');
    return state;
  }

  // A stored kind this build no longer knows (data from before fold-boundary validation) latches
  // update-required instead of bricking open(); the state stays where the fold got to.
  private foldTailOrLatch(base: TState, tail: ReadonlyArray<StoredEvent>): TState {
    try {
      return foldEvents(this.def, base, tail, false).state;
    } catch (e: unknown) {
      if (e instanceof UnknownEventKindError) {
        this.markUpdateRequired();
        return base;
      }
      throw e;
    }
  }

  // The persisted record carries the truth for counters and cursor (a sibling tab may have moved
  // them); appliedLi stays this tab's, because it describes this tab's in-memory state.
  private mergeRecord(persisted: AggregateRecord | undefined): AggregateRecord {
    if (persisted === undefined) return this.record;
    return {
      ...persisted,
      schemaVersion: this.def.schemaVersion,
      serverVersion: Math.max(persisted.serverVersion, this.record.serverVersion),
      nextClientSeq: Math.max(persisted.nextClientSeq, this.record.nextClientSeq),
      lastSentClientSeq: Math.max(persisted.lastSentClientSeq, this.record.lastSentClientSeq),
      lastLi: Math.max(persisted.lastLi, this.record.lastLi),
      appliedLi: this.record.appliedLi,
    };
  }

  private freshRecord(): AggregateRecord {
    return {
      type: this.type, id: this.id,
      schemaVersion: this.def.schemaVersion,
      serverVersion: 0, lastSentClientSeq: 0, nextClientSeq: 0,
      lastLi: 0, appliedLi: 0, trimFloor: 1,
      createdAt: 0, modifiedAt: 0,
    };
  }

  // An unsent row always carries a clientSeq above lastSentClientSeq, so when the two counters
  // meet there is nothing to count and the index scan is skipped.
  private async countPending(): Promise<number> {
    assert(this.record.nextClientSeq >= this.record.lastSentClientSeq, 'countPending: nothing sent beyond what was stamped');
    if (this.record.nextClientSeq === this.record.lastSentClientSeq) return 0;
    const count = await this.store.countUnsent(this.type, this.id);
    assert(count <= this.record.nextClientSeq - this.record.lastSentClientSeq, 'countPending: unsent rows fit between the counters');
    return count;
  }

  private encryptsSomething(): boolean {
    return this.def.encrypt || Object.values(this.def.encryptKinds).some((flag) => flag);
  }

  // Update-required is read-only: an edit computed against a picture this build cannot complete
  // must not leave the device.
  private assertWritable(): void {
    if (this.updateRequiredFlag) throw new UpdateRequiredError(this.type, this.id);
  }

  private markUpdateRequired(): void {
    if (this.updateRequiredFlag) return;
    this.updateRequiredFlag = true;
    this.onUpdateRequired?.();
    this.status.emit();
  }

  private enterSync(): void { this.inFlight += 1; this.status.emit(); }
  private exitSync(): void { this.inFlight -= 1; assert(this.inFlight >= 0, 'exitSync: balanced with enterSync'); this.status.emit(); }
}

// The same event under the same (clientId, clientSeq): creation stamp and type agree, and so does
// the plaintext when the wire carries it. Ciphertext differs per encryption, so it is not compared.
function sameEvent(row: StoredEvent, wire: WireEvent): boolean {
  if (row.clientSeq !== wire.client_seq) return false;
  if (row.ts !== wire.event_timestamp || row.typeMajor !== wire.event_type_major) return false;
  return wire.iv !== undefined || row.value === wire.event_value;
}

function lastLiOf(rows: ReadonlyArray<StoredEvent>, fallback: number): number {
  return rows.length === 0 ? fallback : rows[rows.length - 1]!.li;
}

export class PreconditionFailed extends Error {
  readonly kind: string;
  constructor(kind: string) {
    super(`precondition failed for ${kind}`);
    this.name = 'PreconditionFailed';
    this.kind = kind;
  }
}

export class UpdateRequiredError extends Error {
  readonly type: string;
  readonly id: string;
  constructor(type: string, id: string) {
    super(`aggregate "${type}/${id}" is update-required (read-only): update the app to continue editing`);
    this.name = 'UpdateRequiredError';
    this.type = type;
    this.id = id;
  }
}

// A schema change with no `migrate` on a log the server has trimmed: the trimmed prefix exists
// only inside the old-schema state document, so a replay would silently drop it.
export class CannotReproject extends Error {
  readonly type: string;
  readonly id: string;
  constructor(type: string, id: string, storedVersion: number, definedVersion: number) {
    super(`aggregate "${type}/${id}": state is at schema ${storedVersion}, definition at ${definedVersion}, the log is trimmed and no migrate is declared`);
    this.name = 'CannotReproject';
    this.type = type;
    this.id = id;
  }
}
